import { useEffect, useMemo, useState } from 'react'

const rotatedImageCache = new Map<string, string>()

const normalizeRotation = (rotation: number) => {
  const normalized = rotation % 360
  return normalized < 0 ? normalized + 360 : normalized
}

const getCacheKey = (src: string, rotation: number) => `${src}::${rotation}`

const rotateImage = (src: string, rotation: number): Promise<string> =>
  new Promise(resolve => {
    const image = new Image()
    image.crossOrigin = 'anonymous'

    image.onload = () => {
      try {
        const isQuarterTurn = rotation === 90 || rotation === 270
        const canvas = document.createElement('canvas')
        canvas.width = isQuarterTurn ? image.naturalHeight : image.naturalWidth
        canvas.height = isQuarterTurn ? image.naturalWidth : image.naturalHeight

        const ctx = canvas.getContext('2d')
        if (!ctx) {
          resolve(src)
          return
        }

        ctx.translate(canvas.width / 2, canvas.height / 2)
        ctx.rotate((rotation * Math.PI) / 180)
        ctx.drawImage(image, -image.naturalWidth / 2, -image.naturalHeight / 2)

        resolve(canvas.toDataURL())
      } catch {
        resolve(src)
      }
    }

    image.onerror = () => resolve(src)
    image.src = src
  })

export const useRotatedImageSrc = (src: string, rotation: number) => {
  const [resolvedSrc, setResolvedSrc] = useState(src)
  const normalizedRotation = useMemo(
    () => normalizeRotation(rotation),
    [rotation]
  )

  useEffect(() => {
    let isCancelled = false

    if (!src || normalizedRotation === 0) {
      setResolvedSrc(src)
      return () => {
        isCancelled = true
      }
    }

    const cacheKey = getCacheKey(src, normalizedRotation)
    const cachedValue = rotatedImageCache.get(cacheKey)
    if (cachedValue) {
      setResolvedSrc(cachedValue)
      return () => {
        isCancelled = true
      }
    }

    rotateImage(src, normalizedRotation).then(result => {
      if (isCancelled) return
      rotatedImageCache.set(cacheKey, result)
      setResolvedSrc(result)
    })

    return () => {
      isCancelled = true
    }
  }, [src, normalizedRotation])

  return resolvedSrc
}
